import { Component, Input, OnInit, ViewChild } from "@angular/core";
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from "@angular/forms";
import German from "flatpickr/dist/l10n/de";
import { v4 as uuidv4 } from "uuid";
import monthSelectPlugin from "flatpickr/dist/plugins/monthSelect";
import { getDateTime } from "../../funktionen/globale-funktionen";
import { FlatpickrDefaultsInterface, FlatpickrDirective } from "angularx-flatpickr";
import { DateTime } from "luxon";

@Component({
  selector: "love-it-date-picker",
  templateUrl: "./date-picker.component.html",
  styleUrls: ["./date-picker.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: DatePickerComponent,
      multi: true,
    },
  ],
  standalone: false,
})
export class DatePickerComponent implements ControlValueAccessor, OnInit {
  @ViewChild(FlatpickrDirective) flatpickr: FlatpickrDirective;

  @Input() clearable: boolean = true;
  @Input() enableTime: boolean = false;
  @Input() monthSelect: boolean = false;
  @Input() readonly: boolean = false;
  @Input() minDate: Date | string = null;
  @Input() maxDate: Date | string = null;
  @Input() setToEndOfDay = false;

  changeFunction: any;
  setDateFormControl = new FormControl(null);
  unique = uuidv4();

  public flatpickrOptions: FlatpickrDefaultsInterface;

  constructor() {}

  ngOnInit(): void {
    this.flatpickrOptions = {
      altInput: true,
      altInputClass: "form-control flat-picker flatpickr-input",
      altFormat: this.enableTime ? "d.m.Y H:i" : "d.m.Y",
      locale: German.de,
      mode: "single",
      disableMobile: true,
      allowInput: true,
      enableTime: this.enableTime,
      time24hr: true,
      ...(this.minDate && { minDate: this.minDate }),
      ...(this.monthSelect && {
        plugins: [
          monthSelectPlugin({
            shorthand: true,
          }),
        ],
      }),
    };
    this.setDateFormControl.valueChanges.subscribe(() => {
      let date = this.setDateFormControl.value;
      this.setDate(date);
    });
  }

  private setDate(date) {
    if (Array.isArray(date)) {
      date = date[0] ?? null;
    }
    if (this.monthSelect && date) {
      const dateTime = getDateTime(date);
      if (dateTime.isValid) {
        date = dateTime.toJSDate();
      } else {
        date = DateTime.fromFormat(date, "MMMM yyyy", { locale: "de-AT" }).toJSDate();
      }
    }
    if (!this.enableTime && date) {
      const dateTime = getDateTime(date).setZone("Europe/Vienna");
      if (this.setToEndOfDay) {
        date = dateTime.endOf("day").toJSDate();
      } else {
        date = dateTime.startOf("day").toJSDate();
      }
    }
    this.changeFunction(date);
  }

  onOpen({ selectedDates, dateString, instance }) {
    if (this.minDate) {
      instance.set("minDate", this.minDate);
    }
    if (this.maxDate) {
      instance.set("maxDate", this.maxDate);
    }
  }

  registerOnChange(fn: any): void {
    this.changeFunction = fn;
  }

  registerOnTouched(fn: any): void {}

  writeValue(value: any): void {
    if (value) {
      const newDate = getDateTime(value).toJSDate();
      if (newDate != this.setDateFormControl.value?.[0]) {
        this.setDateFormControl.setValue([newDate], { emitEvent: false });
      }
    } else {
      if (this.setDateFormControl.value?.length) {
        this.setDateFormControl.setValue([], { emitEvent: false });
      }
    }
  }

  clearInput() {
    this.setDateFormControl.setValue([]);
  }

  get isEmpty(): boolean {
    return !this.setDateFormControl.value?.length;
  }

  onClose(event: any) {
    this.setDate(event.selectedDates);
  }

  setDisabledState(isDisabled: boolean) {
    this.readonly = isDisabled;
  }
}
